/**
 * FAQ Searchable Component
 * Handles search functionality, dropdown recommendations, and accordion behavior
 */
class FAQSearchable {
  constructor(sectionElement, sectionId) {
    this.section = sectionElement;
    this.sectionId = sectionId;
    this.searchInput = this.section.querySelector(`#faq-search-input-${sectionId}`);
    this.searchDropdown = this.section.querySelector(`#faq-search-dropdown-${sectionId}`);
    this.searchResults = this.searchDropdown.querySelector('.faq-search-results');
    this.faqItems = this.section.querySelectorAll('.faq-accordion-item');
    this.accordionHeaders = this.section.querySelectorAll('.faq-accordion-header');
    this.faqGroups = this.section.querySelectorAll('.faq-group');
    this.groupNavItems = this.section.querySelectorAll('.faq-group-nav-item');
    
    this.searchTimeout = null;
    this.currentHighlightIndex = -1;
    this.searchResultItems = [];
    this.isDropdownOpen = false;
    this.currentActiveGroup = null;
    
    this.init();
  }
  
  init() {
    this.bindEvents();
    this.initializeAccordions();
    this.buildSearchIndex();
    this.initializeGroupNavigation();
  }
  
  bindEvents() {
    // Search input events
    this.searchInput.addEventListener('input', this.handleSearchInput.bind(this));
    this.searchInput.addEventListener('keydown', this.handleSearchKeydown.bind(this));
    this.searchInput.addEventListener('focus', this.handleSearchFocus.bind(this));
    this.searchInput.addEventListener('blur', this.handleSearchBlur.bind(this));
    
    // Accordion events
    this.accordionHeaders.forEach(header => {
      header.addEventListener('click', this.handleAccordionClick.bind(this));
    });
    
    // Group navigation events
    this.groupNavItems.forEach(navItem => {
      navItem.addEventListener('click', this.handleGroupNavClick.bind(this));
    });
    
    // Click outside to close dropdown
    document.addEventListener('click', this.handleDocumentClick.bind(this));
    
    // Escape key to close dropdown
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isDropdownOpen) {
        this.closeDropdown();
        this.searchInput.blur();
      }
    });
  }
  
  buildSearchIndex() {
    this.searchIndex = [];
    this.faqItems.forEach((item, index) => {
      const question = item.dataset.faqQuestion || '';
      const answer = item.dataset.faqAnswer || '';
      const group = item.dataset.faqGroup || 'general';
      
      this.searchIndex.push({
        index,
        element: item,
        question: question.toLowerCase(),
        answer: answer.toLowerCase(),
        questionOriginal: question,
        answerOriginal: answer,
        group: group
      });
    });
  }
  
  
  initializeGroupNavigation() {
    if (this.groupNavItems.length > 0) {
      // Set first group as active by default
      this.groupNavItems[0].classList.add('active');
      this.currentActiveGroup = this.groupNavItems[0].dataset.groupId;
    }
  }
  
  handleGroupNavClick(e) {
    const clickedNavItem = e.currentTarget;
    const targetGroupId = clickedNavItem.dataset.groupId;
    
    // Update active nav item
    this.groupNavItems.forEach(item => item.classList.remove('active'));
    clickedNavItem.classList.add('active');
    
    this.currentActiveGroup = targetGroupId;
    this.scrollToGroup(targetGroupId);
  }
  
  scrollToGroup(groupId) {
    const targetGroup = this.section.querySelector(`#${groupId}`);
    if (!targetGroup) return;
    
    // Add highlight animation to the group
    targetGroup.classList.add('highlight');
    setTimeout(() => {
      targetGroup.classList.remove('highlight');
    }, 2000);
    
    // Scroll to group with offset
    const offset = 120;
    const elementPosition = targetGroup.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - offset;
    
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }
  
  handleSearchInput(e) {
    const query = e.target.value.trim();
    
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      if (query.length > 0) {
        this.performSearch(query);
      } else {
        this.closeDropdown();
      }
    }, 150);
  }
  
  handleSearchKeydown(e) {
    if (!this.isDropdownOpen) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.highlightNextResult();
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.highlightPreviousResult();
        break;
      case 'Enter':
        e.preventDefault();
        this.selectHighlightedResult();
        break;
      case 'Escape':
        e.preventDefault();
        this.closeDropdown();
        this.searchInput.blur();
        break;
    }
  }
  
  handleSearchFocus() {
    if (this.searchInput.value.trim() && this.searchResultItems.length > 0) {
      this.openDropdown();
    }
  }
  
  handleSearchBlur(e) {
    // Delay closing to allow clicking on dropdown items
    setTimeout(() => {
      if (!this.searchDropdown.contains(document.activeElement)) {
        this.closeDropdown();
      }
    }, 150);
  }
  
  handleDocumentClick(e) {
    if (!this.section.contains(e.target)) {
      this.closeDropdown();
    }
  }
  
  performSearch(query) {
    const results = this.searchFAQs(query);
    this.displaySearchResults(results, query);
    
    if (results.length > 0) {
      this.openDropdown();
    } else {
      this.closeDropdown();
    }
  }
  
  searchFAQs(query) {
    const queryLower = query.toLowerCase();
    const results = [];
    
    this.searchIndex.forEach(item => {
      let score = 0;
      let questionMatch = false;
      let answerMatch = false;
      
      // Exact phrase match in question (highest priority)
      if (item.question.includes(queryLower)) {
        score += 100;
        questionMatch = true;
      }
      
      // Exact phrase match in answer
      if (item.answer.includes(queryLower)) {
        score += 50;
        answerMatch = true;
      }
      
      // Word-by-word matching
      const queryWords = queryLower.split(/\s+/).filter(word => word.length > 2);
      queryWords.forEach(word => {
        if (item.question.includes(word)) {
          score += 20;
          questionMatch = true;
        }
        if (item.answer.includes(word)) {
          score += 10;
          answerMatch = true;
        }
      });
      
      // Fuzzy matching for typos (simple implementation)
      if (score === 0) {
        queryWords.forEach(word => {
          if (this.fuzzyMatch(word, item.question) || this.fuzzyMatch(word, item.answer)) {
            score += 5;
            if (this.fuzzyMatch(word, item.question)) questionMatch = true;
            if (this.fuzzyMatch(word, item.answer)) answerMatch = true;
          }
        });
      }
      
      if (score > 0) {
        results.push({
          ...item,
          score,
          questionMatch,
          answerMatch,
          query: queryLower
        });
      }
    });
    
    // Sort by score (descending) and limit results
    return results.sort((a, b) => b.score - a.score).slice(0, 8);
  }
  
  fuzzyMatch(word, text) {
    // Simple fuzzy matching - check if most characters are present
    if (word.length < 3) return false;
    
    let matches = 0;
    for (let char of word) {
      if (text.includes(char)) matches++;
    }
    
    return matches / word.length > 0.7;
  }
  
  displaySearchResults(results, query) {
    this.searchResults.innerHTML = '';
    this.searchResultItems = [];
    this.currentHighlightIndex = -1;
    
    if (results.length === 0) {
      const noResults = document.createElement('div');
      noResults.className = 'faq-search-no-results';
      noResults.textContent = 'No matching FAQs found';
      this.searchResults.appendChild(noResults);
      return;
    }
    
    results.forEach((result, index) => {
      const resultItem = this.createSearchResultItem(result, query, index);
      this.searchResults.appendChild(resultItem);
      this.searchResultItems.push(resultItem);
    });
  }
  
  createSearchResultItem(result, query, index) {
    const item = document.createElement('div');
    item.className = 'faq-search-result-item';
    item.dataset.faqIndex = result.index;
    
    const question = document.createElement('div');
    question.className = 'faq-search-result-question';
    question.innerHTML = this.highlightText(result.questionOriginal, query);
    
    const preview = document.createElement('div');
    preview.className = 'faq-search-result-preview';
    const previewText = this.truncateText(result.answerOriginal, 100);
    preview.innerHTML = this.highlightText(previewText, query);
    
    item.appendChild(question);
    item.appendChild(preview);
    
    // Click event
    item.addEventListener('click', () => {
      this.selectSearchResult(result.index);
    });
    
    // Mouse events for highlighting
    item.addEventListener('mouseenter', () => {
      this.highlightResult(index);
    });
    
    return item;
  }
  
  highlightText(text, query) {
    if (!query) return text;
    
    const queryWords = query.toLowerCase().split(/\s+/).filter(word => word.length > 1);
    let highlightedText = text;
    
    queryWords.forEach(word => {
      const regex = new RegExp(`(${this.escapeRegExp(word)})`, 'gi');
      highlightedText = highlightedText.replace(regex, '<mark>$1</mark>');
    });
    
    return highlightedText;
  }
  
  escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
  
  truncateText(text, maxLength) {
    if (text.length <= maxLength) return text;
    
    const truncated = text.substr(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    
    return lastSpace > 0 ? truncated.substr(0, lastSpace) + '...' : truncated + '...';
  }
  
  highlightNextResult() {
    if (this.searchResultItems.length === 0) return;
    
    this.currentHighlightIndex = (this.currentHighlightIndex + 1) % this.searchResultItems.length;
    this.updateHighlight();
  }
  
  highlightPreviousResult() {
    if (this.searchResultItems.length === 0) return;
    
    this.currentHighlightIndex = this.currentHighlightIndex <= 0 
      ? this.searchResultItems.length - 1 
      : this.currentHighlightIndex - 1;
    this.updateHighlight();
  }
  
  highlightResult(index) {
    this.currentHighlightIndex = index;
    this.updateHighlight();
  }
  
  updateHighlight() {
    this.searchResultItems.forEach((item, index) => {
      item.classList.toggle('highlighted', index === this.currentHighlightIndex);
    });
  }
  
  selectHighlightedResult() {
    if (this.currentHighlightIndex >= 0 && this.searchResultItems[this.currentHighlightIndex]) {
      const faqIndex = parseInt(this.searchResultItems[this.currentHighlightIndex].dataset.faqIndex);
      this.selectSearchResult(faqIndex);
    }
  }
  
  selectSearchResult(faqIndex) {
    this.closeDropdown();
    this.searchInput.value = '';
    this.scrollToFAQ(faqIndex);
  }
  
  scrollToFAQ(faqIndex) {
    const faqItem = this.faqItems[faqIndex];
    if (!faqItem) return;
    
    // Add highlight animation
    faqItem.classList.add('highlight');
    setTimeout(() => {
      faqItem.classList.remove('highlight');
    }, 2000);
    
    // Scroll to FAQ with offset
    const offset = 100;
    const elementPosition = faqItem.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - offset;
    
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
    
    // Open the accordion after a short delay
    setTimeout(() => {
      this.openAccordion(faqItem);
    }, 200);
  }
  
  openDropdown() {
    this.searchDropdown.classList.add('active');
    this.isDropdownOpen = true;
  }
  
  closeDropdown() {
    this.searchDropdown.classList.remove('active');
    this.isDropdownOpen = false;
    this.currentHighlightIndex = -1;
    this.updateHighlight();
  }
  
  initializeAccordions() {
    this.faqItems.forEach(item => {
      const header = item.querySelector('.faq-accordion-header');
      const content = item.querySelector('.faq-accordion-content');
      
      // Set initial ARIA attributes
      const isExpanded = item.classList.contains('active');
      header.setAttribute('aria-expanded', isExpanded);
      content.setAttribute('aria-hidden', !isExpanded);
    });
  }
  
  handleAccordionClick(e) {
    const header = e.currentTarget;
    const faqItem = header.closest('.faq-accordion-item');
    
    this.toggleAccordion(faqItem);
  }
  
  toggleAccordion(faqItem) {
    const isActive = faqItem.classList.contains('active');
    const header = faqItem.querySelector('.faq-accordion-header');
    const content = faqItem.querySelector('.faq-accordion-content');

    if (isActive) {
      this.closeAccordion(faqItem);
    } else {
      this.openAccordion(faqItem);
    }
  }
  
  openAccordion(faqItem) {
    const header = faqItem.querySelector('.faq-accordion-header');
    const content = faqItem.querySelector('.faq-accordion-content');
    
    // Close other accordions if you want only one open at a time
    // this.closeAllAccordions();
    
    // Add active classes
    faqItem.classList.add('active');
    content.classList.add('active');
    header.setAttribute('aria-expanded', 'true');
    content.setAttribute('aria-hidden', 'false');
    
    // Calculate and set the target height
    const scrollHeight = content.scrollHeight;
    content.style.maxHeight = scrollHeight + 'px';
    
    // Add a listener to clean up max-height after animation completes
    const handleTransitionEnd = (e) => {
      if (e.target === content && e.propertyName === 'max-height') {
        if (faqItem.classList.contains('active')) {
          content.style.maxHeight = 'none';
        }
        content.removeEventListener('transitionend', handleTransitionEnd);
      }
    };
    
    content.addEventListener('transitionend', handleTransitionEnd);
  }
  
  closeAccordion(faqItem) {
    const header = faqItem.querySelector('.faq-accordion-header');
    const content = faqItem.querySelector('.faq-accordion-content');
    
    // Set the current height first to enable smooth closing
    if (content.style.maxHeight === 'none') {
      content.style.maxHeight = content.scrollHeight + 'px';
    }
    
    // Force reflow to ensure the height is set
    content.offsetHeight;
    
    // Remove active classes
    faqItem.classList.remove('active');
    content.classList.remove('active');
    header.setAttribute('aria-expanded', 'false');
    content.setAttribute('aria-hidden', 'true');
    
    // Animate to 0 height
    requestAnimationFrame(() => {
      content.style.maxHeight = '0px';
    });
    
    // Clean up after animation
    const handleTransitionEnd = (e) => {
      if (e.target === content && e.propertyName === 'max-height') {
        if (!faqItem.classList.contains('active')) {
          content.style.maxHeight = '';
        }
        content.removeEventListener('transitionend', handleTransitionEnd);
      }
    };
    
    content.addEventListener('transitionend', handleTransitionEnd);
  }
  
  closeAllAccordions() {
    this.faqItems.forEach(item => {
      if (item.classList.contains('active')) {
        this.closeAccordion(item);
      }
    });
  }
  
  // Public method to open specific FAQ by question text
  openFAQByQuestion(questionText) {
    const faqItem = Array.from(this.faqItems).find(item => {
      const question = item.dataset.faqQuestion || '';
      return question.toLowerCase().includes(questionText.toLowerCase());
    });
    
    if (faqItem) {
      const index = Array.from(this.faqItems).indexOf(faqItem);
      this.scrollToFAQ(index);
    }
  }
  
  // Public method to search and highlight results
  searchAndHighlight(query) {
    this.searchInput.value = query;
    this.performSearch(query);
  }
}

// Auto-initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  // Initialize all FAQ searchable sections on the page
  const faqSections = document.querySelectorAll('.faq-searchable-section');
  
  faqSections.forEach(section => {
    const sectionId = section.id.replace('faq-searchable-', '');
    new FAQSearchable(section, sectionId);
  });
});

// Make FAQSearchable globally available
window.FAQSearchable = FAQSearchable;
